/**
 * @name Router Configuration
 * @description Configuração principal das rotas usando React Router DOM v7 - Versão com controle de operador
 * @author Keven Adan Bezerra | kevenadanb
 */

import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

// Pages
import SessionRecoveryLoading from "@/components/common/SessionRecoveryLoading";
import { LoginOperador, TotemHomePage } from "@/components/pages";
import { CarrinhoProvider } from "@/contexts/CarrinhoContext";
import {
  OperadorProvider,
  useOperadorContext,
} from "@/contexts/OperadorContext";
import { PedidoProvider } from "@/contexts/PedidoContext";
import { ProdutoProvider } from "@/contexts/ProdutoContext";
import { TotemProvider } from "@/contexts/TotemContext";
import { UserProvider } from "@/contexts/UserContext";

// Componente de proteção que verifica se o totem está liberado
const ProtectedTotemRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isTotemLiberado, isRecoveringSession } = useOperadorContext();

  // Enquanto estiver recuperando a sessão, mostra um loading
  if (isRecoveringSession) {
    return <SessionRecoveryLoading />;
  }

  // Se o totem não está liberado, mostra a tela de login do operador
  if (!isTotemLiberado) {
    return <LoginOperador />;
  }

  // Se está liberado, mostra o conteúdo protegido
  return <>{children}</>;
};

const ProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <OperadorProvider>
      <TotemProvider>
        <UserProvider>
          <ProdutoProvider>
            <CarrinhoProvider>
              <PedidoProvider>
                <ProtectedTotemRoute>{children}</ProtectedTotemRoute>
              </PedidoProvider>
            </CarrinhoProvider>
          </ProdutoProvider>
        </UserProvider>
      </TotemProvider>
    </OperadorProvider>
  );
};

/**
 * Configuração das rotas da aplicação - Versão com controle de operador
 */
export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProviderWrapper>
        <TotemHomePage />
      </ProviderWrapper>
    ),
  },
  {
    path: "*",
    element: (
      <ProviderWrapper>
        <TotemHomePage />
      </ProviderWrapper>
    ),
  },
]);

/**
 * Provider principal do router
 */
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
